import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { BACKEND_BASE_URL } from "../../config/app.config";
import { StateContext } from "../../store/context";
import ROUTES from "../../setup/routes";
import "./AnnualReportWorkflowHistory.css";

const resolveBaseUrl = () => {
  const envUrl =
    process.env.REACT_APP_ANNUAL_REPORT_API ??
    process.env.REACT_APP_API_URL ??
    process.env.REACT_APP_BASE_URL ??
    "";
  if (typeof window !== "undefined") {
    const origin = window.location.origin || "";
    const isHttps = window.location.protocol === "https:";
    const candidate = (envUrl || BACKEND_BASE_URL || "").replace(/\/+$/, "");

    if (isHttps && candidate.startsWith("http://")) {
      if (origin && !origin.includes("localhost")) {
        return origin;
      }
      return candidate.replace(/^http:\/\//, "https://");
    }

    if (candidate) return candidate;
    if (origin) return origin;
  }
  return (envUrl || BACKEND_BASE_URL || "http://localhost:8000").replace(/\/+$/, "");
};

const API_BASE_URL = resolveBaseUrl();
const toAuthorizationHeader = (token: any) => {
  const trimmed = String(token || "").trim();
  if (!trimmed) {
    return "";
  }
  return /^bearer\s+/i.test(trimmed) ? trimmed : `Bearer ${trimmed}`;
};

type WorkflowRow = {
  id: number;
  stock_id: number;
  ticker_symbol: string;
  fiscal_year: number;
  quarter_date: string;
  quarter_label: string;
  status: string;
  created_by?: number;
  created_by_username?: string;
  created_by_email?: string;
  created_by_full_name?: string;
  versions?: any[];
  created_at?: string;
  updated_at?: string;
};

const AnnualReportWorkflowHistory: React.FC = () => {
  const { authUser } = useContext(StateContext);
  const navigate = useNavigate();
  const [stockOptions, setStockOptions] = useState<any[]>([]);
  const [stockSearchTerm, setStockSearchTerm] = useState("");
  const [selectedTicker, setSelectedTicker] = useState("");
  const [fiscalYear, setFiscalYear] = useState("");
  const [quarterDate, setQuarterDate] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [rows, setRows] = useState<WorkflowRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionWorkflowId, setActionWorkflowId] = useState<number | null>(null);
  const [actionType, setActionType] = useState<"delete" | "">("");
  const [message, setMessage] = useState("");

  const authHeaders = useMemo(() => {
    const headers: any = {};
    const authToken = localStorage.getItem("token");
    const authorization = toAuthorizationHeader(authToken);
    if (authorization) {
      headers.Authorization = authorization;
    }
    if (authUser?.role_id) {
      headers.role_id = String(authUser.role_id);
    }
    if (authUser?.email) {
      headers["x-user-email"] = String(authUser.email);
    }
    if (authUser?.username) {
      headers["x-username"] = String(authUser.username);
    }
    return headers;
  }, [authUser?.role_id, authUser?.email, authUser?.username]);

  const stockAutocompleteOptions = useMemo(
    () =>
      (stockOptions || []).map((stock: any) => ({
        ...stock,
        displayLabel: `${stock?.company_name || stock?.ticker_symbol} (${stock?.ticker_symbol})`,
      })),
    [stockOptions]
  );

  const findExactStockMatch = useCallback(
    (rawValue: string) => {
      const normalized = String(rawValue || "").trim().toLowerCase();
      if (!normalized) {
        return null;
      }
      return (
        stockAutocompleteOptions.find((stock: any) => {
          const display = String(stock?.displayLabel || "").trim().toLowerCase();
          const ticker = String(stock?.ticker_symbol || "").trim().toLowerCase();
          const company = String(stock?.company_name || "").trim().toLowerCase();
          return (
            display === normalized ||
            ticker === normalized ||
            company === normalized
          );
        }) || null
      );
    },
    [stockAutocompleteOptions]
  );

  const handleStockSearchChange = useCallback(
    (value: string) => {
      setStockSearchTerm(value);
      const exactMatch = findExactStockMatch(value);
      setSelectedTicker(exactMatch?.ticker_symbol || "");
    },
    [findExactStockMatch]
  );

  const handleStockSearchBlur = useCallback(() => {
    const normalized = String(stockSearchTerm || "").trim().toLowerCase();
    if (!normalized) {
      setSelectedTicker("");
      return;
    }

    const exactMatch = findExactStockMatch(stockSearchTerm);
    if (exactMatch?.ticker_symbol) {
      setSelectedTicker(exactMatch.ticker_symbol);
      setStockSearchTerm(exactMatch.displayLabel);
      return;
    }

    const candidates = stockAutocompleteOptions.filter((stock: any) => {
      const ticker = String(stock?.ticker_symbol || "").toLowerCase();
      const company = String(stock?.company_name || "").toLowerCase();
      return ticker.includes(normalized) || company.includes(normalized);
    });

    if (candidates.length === 1) {
      setSelectedTicker(candidates[0].ticker_symbol);
      setStockSearchTerm(candidates[0].displayLabel);
    }
  }, [findExactStockMatch, stockAutocompleteOptions, stockSearchTerm]);

  const loadStocks = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/all-stocks`, {
        headers: authHeaders,
      });
      const sections = response?.data?.data || response?.data || [];
      const flattened = Array.isArray(sections)
        ? sections.flatMap((section: any) => section?.options || [])
        : [];
      const deduped = new Map();
      flattened.forEach((stock: any) => {
        const ticker = String(stock?.ticker_symbol || "").trim().toUpperCase();
        if (!ticker || deduped.has(ticker)) {
          return;
        }
        deduped.set(ticker, {
          id: stock?.id ?? null,
          ticker_symbol: ticker,
          company_name: stock?.company_name || ticker,
        });
      });
      const options = Array.from(deduped.values())
        .filter((stock: any) => stock?.ticker_symbol && stock?.id)
        .sort((a: any, b: any) =>
          `${a.company_name} (${a.ticker_symbol})`.localeCompare(
            `${b.company_name} (${b.ticker_symbol})`
          )
        );
      setStockOptions(options);
    } catch (error) {
      setStockOptions([]);
    }
  }, [authHeaders]);

  const loadHistory = useCallback(async () => {
    setLoading(true);
    setMessage("");
    try {
      const fiscalYearNumeric = Number(fiscalYear || 0);
      const response = await axios.get(
        `${API_BASE_URL}/api/annual-report/workflows/history`,
        {
          headers: authHeaders,
          params: {
            ticker_symbol: selectedTicker || undefined,
            fiscal_year:
              Number.isInteger(fiscalYearNumeric) && fiscalYearNumeric > 0
                ? fiscalYearNumeric
                : undefined,
            quarter_date: quarterDate || undefined,
            limit: 200,
          },
        }
      );
      const historyRows = Array.isArray(response?.data?.rows) ? response.data.rows : [];
      const filteredRows =
        statusFilter === "ALL"
          ? historyRows
          : historyRows.filter(
              (row: any) =>
                String(row?.status || "").toUpperCase() === statusFilter
            );
      setRows(filteredRows);
    } catch (error: any) {
      const serverMessage = error?.response?.data?.error || error?.response?.data?.details;
      setRows([]);
      setMessage(serverMessage || "Failed to load workflow history.");
    } finally {
      setLoading(false);
    }
  }, [authHeaders, selectedTicker, fiscalYear, quarterDate, statusFilter]);

  useEffect(() => {
    loadStocks();
  }, [loadStocks]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const getCurrentVersion = (row: WorkflowRow) => {
    const versions = Array.isArray(row?.versions) ? row.versions : [];
    return versions.find((version) => Number(version?.is_current || 0) === 1) || versions[0] || null;
  };

  const getSubmittedByLabel = useCallback((row: WorkflowRow) => {
    const username = String((row as any)?.created_by_username || "").trim();
    if (username) {
      return username;
    }
    const email = String((row as any)?.created_by_email || "").trim();
    if (email) {
      return email;
    }
    const userId = Number((row as any)?.created_by || 0);
    return userId > 0 ? `User #${userId}` : "-";
  }, []);

  const openInEditor = (row: WorkflowRow) => {
    const currentVersion = getCurrentVersion(row);
    const params = new URLSearchParams();
    params.set("workflow_id", String(row.id));
    if (currentVersion?.id) {
      params.set("version_id", String(currentVersion.id));
    }
    navigate(`/${ROUTES.ANNUAL_REPORT_EXTRACTION}?${params.toString()}`);
  };

  const deleteWorkflow = async (row: WorkflowRow) => {
    if (!row?.id) {
      setMessage("Workflow is missing.");
      return;
    }
    const status = String(row?.status || "").trim().toUpperCase();
    if (status === "COMMITTED") {
      setMessage("Committed workflows cannot be deleted.");
      return;
    }

    const confirmed = window.confirm(
      `Delete workflow #${row.id} for ${row.ticker_symbol || "-"} (${row.quarter_label || row.quarter_date || "-"})?\n\nThis will permanently remove all versions and cannot be undone.`
    );
    if (!confirmed) {
      return;
    }

    setActionLoading(true);
    setActionWorkflowId(Number(row.id || 0));
    setActionType("delete");
    setMessage("");
    try {
      await axios.post(
        `${API_BASE_URL}/api/annual-report/workflows/delete`,
        {
          workflow_id: row.id,
        },
        { headers: authHeaders }
      );
      setMessage(`Workflow #${row.id} was deleted successfully.`);
      await loadHistory();
    } catch (error: any) {
      const serverMessage = error?.response?.data?.error || error?.response?.data?.details;
      setMessage(serverMessage || "Failed to delete workflow.");
    } finally {
      setActionLoading(false);
      setActionWorkflowId(null);
      setActionType("");
    }
  };

  return (
    <div className="falcon-history-page">
      <div className="falcon-history-card">
        <h2>Falcon Data Entry History</h2>
        <p>
          Data entry users can open previous submissions and recheck/edit values in Falcon editor before submitting updated versions for QC.
        </p>

        <div className="falcon-history-filters">
          <div className="falcon-history-filter">
            <label htmlFor="falcon-history-stock-search">Stock</label>
            <input
              id="falcon-history-stock-search"
              type="text"
              list="falcon-history-stock-options"
              value={stockSearchTerm}
              onChange={(event) => handleStockSearchChange(event.target.value)}
              onBlur={handleStockSearchBlur}
              placeholder="Type ticker or company"
            />
            <datalist id="falcon-history-stock-options">
              {stockAutocompleteOptions.map((stock: any) => (
                <option key={stock.ticker_symbol} value={stock.displayLabel} />
              ))}
            </datalist>
          </div>

          <div className="falcon-history-filter">
            <label>Fiscal Year</label>
            <input
              type="text"
              value={fiscalYear}
              maxLength={4}
              onChange={(event) => setFiscalYear(event.target.value.replace(/[^0-9]/g, ""))}
              placeholder="YYYY"
            />
          </div>

          <div className="falcon-history-filter">
            <label>Quarter Date</label>
            <input
              type="date"
              value={quarterDate}
              onChange={(event) => setQuarterDate(event.target.value)}
            />
          </div>

          <div className="falcon-history-filter">
            <label>Status</label>
            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
            >
              <option value="ALL">All</option>
              <option value="PENDING_QC">Pending QC</option>
              <option value="REJECTED">Rejected</option>
              <option value="APPROVED">Approved</option>
              <option value="COMMITTED">Committed</option>
            </select>
          </div>

          <div className="falcon-history-actions">
            <button type="button" onClick={loadHistory} disabled={loading}>
              {loading ? "Loading..." : "Refresh"}
            </button>
          </div>
        </div>

        {message ? <div className="falcon-history-message">{message}</div> : null}

        <div className="falcon-history-table-wrap">
          <table className="falcon-history-table">
            <thead>
              <tr>
                <th>Workflow</th>
                <th>Stock</th>
                <th>FY</th>
                <th>Quarter</th>
                <th>Submitted By</th>
                <th>Date Added</th>
                <th>Status</th>
                <th>Versions</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {!rows.length ? (
                <tr>
                  <td colSpan={9}>No workflow history found for current filters.</td>
                </tr>
              ) : (
                rows.map((row) => {
                  const rowActionBusy = actionLoading && actionWorkflowId === Number(row.id || 0);
                  const rowStatus = String(row?.status || "").trim().toUpperCase();
                  const canDelete = rowStatus !== "COMMITTED";
                  return (
                    <tr key={row.id}>
                      <td>{row.id}</td>
                      <td>{row.ticker_symbol || "-"}</td>
                      <td>{row.fiscal_year || "-"}</td>
                      <td>{row.quarter_label || row.quarter_date || "-"}</td>
                      <td>{getSubmittedByLabel(row)}</td>
                      <td>{row.created_at ? new Date(row.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : "-"}</td>
                      <td>{String(row.status || "-").replace(/_/g, " ")}</td>
                      <td>{Array.isArray(row.versions) ? row.versions.length : 0}</td>
                      <td>
                        <div className="falcon-history-row-actions">
                          <button type="button" onClick={() => openInEditor(row)} disabled={actionLoading}>
                            Open In Editor
                          </button>
                          <button
                            type="button"
                            className="danger"
                            onClick={() => deleteWorkflow(row)}
                            disabled={actionLoading || !canDelete}
                          >
                            {rowActionBusy && actionType === "delete" ? "Deleting..." : "Delete"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AnnualReportWorkflowHistory;
